import React, { Fragment } from 'react';
import { modalStore } from 'components/layout/modals/ModalStore';
import { Field, FieldArray, Formik } from 'formik';
import * as Yup from 'yup';
import { FormField as FormikFormField } from 'components/layout/form-fields/FormField';
import { notificationStore } from 'components/layout/notifications/NotificationStore';
import { ModalComponent } from 'components/layout/modals/ModalComponent';
import { clientStore } from '../ClientStore';
import { UsersDropdown } from 'components/layout/form-fields/inputs/UsersDropdown';
import { CountryDropdown } from 'components/layout/form-fields/inputs/CountryDropdown';
import { SkillDropdown } from 'components/layout/form-fields/inputs/SkillDropdown';
import { JobRoleDropdown } from 'components/layout/form-fields/inputs/JobRoleDropdown';
import { USER_ROLES } from 'shared/security/RolesAllowed';
import {
	Button,
	FormColumn,
	FormField,
	FormRow,
	FormSection
} from 'components/layout/modals/components';
import { ProfileImageInput } from 'components/layout/form-fields/inputs/ProfileImageInput';
import HoverMenu from 'ats/components/hover-menu/HoverMenu';
import { copyTextToClipboard } from 'ats/utils/copyToClipboard';
import ActionMenu from 'ats/components/action-menu/ActionMenu';
import DropdownSearchable from 'ats/components/dropdown-searchable/DropdownSearchable';
import { ClientContactForm } from './ClientContactForm';
import { openClientProfile } from 'ats/utils/openEntityProfile';

const SUPPORTED_FORMATS = ['image/jpg', 'image/jpeg', 'image/gif', 'image/png'];
const MAX_FILE_SIZE_LIMIT = 1000 * 1024;

const ValidationSchema = Yup.object().shape({
	name: Yup.string().required().label('Name'),
	industry: Yup.object().nullable().required().label('Industry'),
	size: Yup.number().required().label('Size'),
	countryId: Yup.string().nullable().required().label('Country'),
	city: Yup.string().required().label('City'),
	accountManagerId: Yup.string().nullable().required().label('Account Manager'),
	linkedinUrl: Yup.string().label('Linkedin URL'),
	website: Yup.string().url().label('Website'),
	technologies: Yup.array().label('Technologies'),
	jobRoles: Yup.array().label('Job Roles'),
	profileImage: Yup.mixed()
		.nullable()
		.test(
			'only-image-files',
			'File type not supported. Use .png, .jpg, .jpeg or .gif.',
			value => {
				return value ? SUPPORTED_FORMATS.includes(value.type) : true;
			}
		)
		.test(
			'file-size-limit',
			'The image file size is too large (max 1MB)',
			value => (value ? value.size <= MAX_FILE_SIZE_LIMIT : true)
		)
		.label('Profile Image'),
	clientContacts: Yup.array().of(
		Yup.object().shape({
			isPrimary: Yup.boolean().required().label('Is Primary'),
			name: Yup.string().required().label('Name'),
			role: Yup.string().required().label('Role'),
			slackId: Yup.string().nullable().label('Slack ID'),
			email: Yup.string().email().nullable().label('Email'),
			phone: Yup.string().nullable().label('Phone')
		})
	)
});

export class ClientCreate extends ModalComponent {
	static isV2 = true;
	state = {
		isAddingNewContact: false,
		editingClientContactIndex: null
	};

	render() {
		return (
			<Formik
				initialValues={{
					name: '',
					industry: null,
					size: '',
					countryId: null,
					country: null,
					city: '',
					accountManagerId: null,
					accountManager: null,
					linkedinUrl: '',
					website: '',
					profileImage: null,
					technologies: [],
					jobRoles: [],
					clientContacts: []
				}}
				validationSchema={ValidationSchema}
				validateOnBlur={false}
				validateOnChange={false}
				onSubmit={values => {
					this.saving();
					this.create(values);
				}}>
				{formikBag => this.renderModalContainer(formikBag, 'ClientCreate')}
			</Formik>
		);
	}

	renderForm(formikBag) {
		const isEditingContact =
			this.state.editingClientContactIndex !== null &&
			this.state.editingClientContactIndex !== undefined;

		return (
			<Fragment>
				<FormField
					variant="blue"
					label={
						<img src="/ats/icons/linkedin-blue-icon.svg" alt="LinkedIn" />
					}>
					<FormikFormField
						className="has-text-left"
						placeholder="https://linkedin.com/in/"
						field="linkedinUrl"
						type="text"
					/>
				</FormField>
				<FormRow>
					<FormSection size={1 / 2}>
						<Field
							component={ProfileImageInput}
							name="profileImage"
							size={120}
							placeholder={
								formikBag.values.name?.trim()
									? formikBag.values.name.trim()[0].toUpperCase()
									: 'C'
							}
							className="mx-auto"
						/>
						<FormField
							label="Name"
							required
							error={formikBag.errors.name}
							variant="dashed">
							<FormikFormField
								className="has-text-left"
								field="name"
								type="text"
								placeholder=" "
								showErrors={false}
							/>
						</FormField>
						<FormField
							label="Industry"
							required
							error={formikBag.errors.industry}
							variant="dashed">
							<DropdownSearchable
								value={formikBag.values.industry}
								placeholder=" "
								onChange={item => {
									formikBag.setFieldValue('industry', item);
								}}
								options={clientStore.industryOptions}
								styleVersion="ats"
							/>
						</FormField>
						<FormField
							label="Size"
							required
							error={formikBag.errors.size}
							variant="dashed">
							<FormikFormField
								className="has-text-left"
								field="size"
								type="number"
								placeholder=" "
								showErrors={false}
							/>
						</FormField>
						<FormField
							label="Country"
							required
							variant="dashed"
							error={formikBag.errors.countryId}>
							<CountryDropdown
								value={formikBag.values.country}
								placeholder=" "
								styleVersion="ats"
								onValueChange={value => {
									formikBag.setFieldValue('country', value);
									formikBag.setFieldValue('countryId', value?.id);
								}}
							/>
						</FormField>
						<FormField
							label="City"
							required
							error={formikBag.errors.city}
							variant="dashed">
							<FormikFormField
								className="has-text-left"
								field="city"
								type="text"
								placeholder=" "
								showErrors={false}
							/>
						</FormField>
						<FormField
							label="Account Manager"
							required
							variant="dashed"
							error={formikBag.errors.accountManagerId}>
							<UsersDropdown
								value={formikBag.values.accountManager}
								placeholder=" "
								role={USER_ROLES.ACCOUNT_MANAGER}
								minSearchChars={0}
								onValueChange={value => {
									formikBag.setFieldValue('accountManager', value);
									formikBag.setFieldValue(
										'accountManagerId',
										value?.id || null
									);
								}}
							/>
						</FormField>
						<FormField
							label="Website"
							error={formikBag.errors.website}
							variant="dashed">
							<FormikFormField
								className="has-text-left"
								field="website"
								type="text"
								placeholder=" "
								showErrors={false}
							/>
						</FormField>
					</FormSection>
					<FormColumn>
						<FormSection
							title="Client Contacts"
							actionButton={
								<Button
									variant="no-style"
									onClick={e => {
										e.preventDefault();

										this.setState({
											isAddingNewContact: true
										});
									}}>
									<img src="/ats/icons/plus-blue-icon.svg" alt="New Contact" />{' '}
									New Contact
								</Button>
							}>
							{(isEditingContact || this.state.isAddingNewContact) && (
								<ClientContactForm
									data={
										isEditingContact
											? formikBag.values.clientContacts[
													this.state.editingClientContactIndex
												]
											: undefined
									}
									onSubmit={values => {
										const updatedContacts =
											formikBag.values.clientContacts.filter(
												(_, idx) => idx !== this.state.editingClientContactIndex
											);

										const hasPrimaryContact = updatedContacts.some(
											contact => contact.isPrimary
										);

										if (!hasPrimaryContact) {
											values.isPrimary = true;
										}

										if (values.isPrimary) {
											updatedContacts.forEach(contact => {
												contact.isPrimary = false;
											});
										}

										formikBag.setFieldValue('clientContacts', [
											...updatedContacts,
											values
										]);
										this.setState({
											isAddingNewContact: false,
											editingClientContactIndex: null
										});
									}}
									customButtons={[
										{
											label: 'Cancel',
											variant: 'secondary',
											onClick: formikBag => {
												formikBag.resetForm();
												this.setState({
													editingClientContactIndex: null,
													isAddingNewContact: false
												});
											}
										},
										{
											label: 'Save',
											variant: 'primary',
											onClick: formikBag => {
												formikBag.submitForm();
											}
										}
									]}
								/>
							)}
							<table className="table-field table is-fullwidth">
								<thead>
									<tr>
										<th style={{ width: '35px' }} />
										<th>Name</th>
										<th>Role</th>
										<th className="has-text-centered">Slack ID</th>
										<th className="has-text-centered">Email</th>
										<th className="has-text-centered">Phone</th>
										<th style={{ width: '35px' }} />
									</tr>
								</thead>
								<tbody>
									<FieldArray name="clientContacts">
										{({ remove }) =>
											formikBag.values.clientContacts.map(
												(clientContact, idx) => {
													const isEditing =
														this.state.editingClientContactIndex === idx;
													return (
														<tr key={idx}>
															<td>
																<button
																	className="set-primary-btn"
																	title="Primary"
																	onClick={e => {
																		e.preventDefault();

																		const updatedContacts =
																			formikBag.values.clientContacts.map(
																				(contact, i) => ({
																					...contact,
																					isPrimary:
																						i === idx
																							? !contact.isPrimary
																							: false
																				})
																			);

																		formikBag.setFieldValue(
																			'clientContacts',
																			updatedContacts
																		);
																	}}
																	type="button">
																	<img
																		src={
																			clientContact.isPrimary
																				? '/ats/icons/star-filled.svg'
																				: '/ats/icons/star-empty.svg'
																		}
																		alt="Star"
																	/>
																</button>
															</td>
															<td>{clientContact.name}</td>
															<td>{clientContact.role}</td>
															<td className="slack-id-column">
																{clientContact.slackId ? (
																	<ActionMenu
																		buttonIcon={
																			<img
																				src="/ats/icons/slack-icon.svg"
																				alt="Slack"
																			/>
																		}
																		tooltipText={clientContact.slackId}
																		items={[
																			{
																				label: 'Copy Slack ID',
																				img: '/ats/icons/copy-icon.svg',
																				onClick: () =>
																					copyTextToClipboard(
																						clientContact.slackId
																					),
																				successLabel: 'Slack ID Copied',
																				successImg: '/ats/icons/check-icon.svg',
																				resetDelay: 2000
																			},
																			{
																				label: 'Send Slack message',
																				img: '/ats/icons/send-icon.svg',
																				onClick: () =>
																					window.open(
																						`https://slack.com/app_redirect?channel=${clientContact.slackId}`,
																						'_blank'
																					)
																			}
																		]}
																	/>
																) : (
																	<div className="is-flex is-justify-content-center">
																		<img
																			src="/ats/icons/slack-inactive-icon.svg"
																			alt="Slack"
																		/>
																	</div>
																)}
															</td>
															<td className="email-column">
																{clientContact.email ? (
																	<ActionMenu
																		buttonIcon={
																			<img
																				src="/ats/icons/email-2-icon.svg"
																				alt="Email"
																			/>
																		}
																		tooltipText={clientContact.email}
																		items={[
																			{
																				label: 'Copy Email',
																				img: '/ats/icons/copy-icon.svg',
																				onClick: () =>
																					copyTextToClipboard(
																						clientContact.email
																					),
																				successLabel: 'Email Copied',
																				successImg: '/ats/icons/check-icon.svg',
																				resetDelay: 2000
																			},
																			{
																				label: 'Send an Email',
																				img: '/ats/icons/send-icon.svg',
																				onClick: () =>
																					window.open(
																						`mailto:${clientContact.email}`,
																						'_blank'
																					)
																			}
																		]}
																	/>
																) : (
																	<div className="is-flex is-justify-content-center">
																		<img
																			src="/ats/icons/email-2-inactive-icon.svg"
																			alt="Email"
																		/>
																	</div>
																)}
															</td>
															<td className="phone-column">
																{clientContact.phone ? (
																	<ActionMenu
																		buttonIcon={
																			<img
																				src="/ats/icons/phone-2-icon.svg"
																				alt="Phone"
																			/>
																		}
																		tooltipText={clientContact.phone}
																		items={[
																			{
																				label: 'Copy Phone number',
																				img: '/ats/icons/copy-icon.svg',
																				onClick: () =>
																					copyTextToClipboard(
																						clientContact.phone
																					),
																				successLabel: 'Phone Copied',
																				successImg: '/ats/icons/check-icon.svg',
																				resetDelay: 2000
																			},
																			{
																				label: 'Make a Phone call',
																				img: '/ats/icons/send-icon.svg',
																				onClick: () =>
																					window.open(
																						`tel:${clientContact.phone}`,
																						'_blank'
																					)
																			}
																		]}
																	/>
																) : (
																	<div className="is-flex is-justify-content-center">
																		<img
																			src="/ats/icons/phone-2-inactive-icon.svg"
																			alt="Phone"
																		/>
																	</div>
																)}
															</td>
															<td>
																{isEditing ? null : (
																	<HoverMenu
																		id={clientContact.id}
																		buttonIcon={
																			<img
																				src="/ats/icons/three-dots-icon.svg"
																				alt="Menu button"
																			/>
																		}
																		items={[
																			{
																				label: 'Edit',
																				onClick: () => {
																					this.setState({
																						editingClientContactIndex: idx
																					});
																				}
																			},
																			{
																				label: 'Delete',
																				onClick: () => {
																					remove(idx);

																					if (
																						clientContact.isPrimary &&
																						formikBag.values.clientContacts
																							.length > 0
																					) {
																						formikBag.setFieldValue(
																							'clientContacts.[0].isPrimary',
																							true
																						);
																					}

																					isEditing &&
																						this.setState({
																							editingClientContactIndex: null
																						});
																				}
																			}
																		]}
																	/>
																)}
															</td>
														</tr>
													);
												}
											)
										}
									</FieldArray>
								</tbody>
							</table>
						</FormSection>
						<FormSection title="Technologies">
							<FormField error={formikBag.errors.technologies}>
								<SkillDropdown
									value={formikBag.values.technologies}
									placeholder=" "
									styleVersion="ats"
									onValueChange={value => {
										formikBag.setFieldValue('technologies', value || []);
									}}
								/>
							</FormField>
						</FormSection>
						<FormSection title="Roles">
							<FormField error={formikBag.errors.jobRoles}>
								<JobRoleDropdown
									value={formikBag.values.jobRoles}
									placeholder=" "
									onValueChange={value => {
										formikBag.setFieldValue('jobRoles', value || []);
									}}
									usePurpleStyles
								/>
							</FormField>
						</FormSection>
					</FormColumn>
				</FormRow>
			</Fragment>
		);
	}

	async create(values) {
		const {
			name,
			accountManagerId,
			industry,
			size,
			countryId,
			city,
			linkedinUrl,
			website,
			profileImage,
			technologies,
			jobRoles,
			clientContacts
		} = values;
		clientStore
			.create({
				name,
				accountManagerId,
				industryId: industry?.value,
				size,
				countryId,
				city,
				linkedinUrl,
				website,
				profileImage,
				technologies,
				jobRoles,
				clientContacts
			})
			.then(result => {
				if (result.status === 'success') {
					notificationStore.pushNotification({
						type: notificationStore.SUCCESS_NOTIFICATION,
						title: 'Create Client',
						message: 'The client has been successfully created.'
					});
					this.closeWithSuccess(result);
					openClientProfile(result.data?.id, {
						onSuccess: () => this.props.handleSuccess?.()
					});
				} else {
					notificationStore.pushNotification({
						type: notificationStore.ERROR_NOTIFICATION,
						title: 'Create Client',
						message: 'An error has occurred. Please try again.'
					});
					modalStore.closeDialog();
				}
			});
	}
}
